import React, { useEffect, useState, useMemo } from "react";
import "./10Variable.css";
import "./10ProjectInfo.css";
import { formAPI } from "../../../../../services/api";
import { formFieldDefs } from "./10formFields";

const VIP10ProjectInfo = ({ projectId }) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    recent7: 0,
    recent30: 0,
    siteCounts: {},
  });

  const DEFAULT_PLAN =
    "본 프로젝트는 2024년 1월부터 2025년 12월까지 총 24개월간 운영될 예정입니다. 분기별 데이터 품질 점검 및 기능 개선이 계획되어 있으며, 추가 요구 사항은 언제든 문의 탭을 통해 요청하실 수 있습니다.";

  // 프로젝트 기본 정보 로드
  useEffect(() => {
    try {
      const storedProject = sessionStorage.getItem("currentProject");
      if (storedProject) {
        setProject(JSON.parse(storedProject));
      }
    } catch (_) {}
  }, []);

  // 하드코딩된 대시보드 위젯 (프로젝트별 수정 가능)
  const DASHBOARD_WIDGETS = useMemo(() => {
    const vars = [
      "sex",
      "witness",
      "arrest_location",
      "immediate_cpr",
      "mech_cpr",
      "ems_1st_rhythm",
      "ems_shock",
      "ems_epi",
      "ems_amio",
      "pre_rosc",
      "ed_disposition",
      "discharge_type",
      "discharge_cpc",
      "1month_cpc",
      "3month_cpc",
      "6month_cpc",
      "12month_cpc",
    ];

    const pies = vars.map((v) => ({
      id: `${v}_pie`,
      title: v.replace(/_/g, " ").toUpperCase(),
      chartType: "pie",
      variable: v,
    }));

    // Custom chart (기관별 데이터 현황)
    const custom = {
      id: "site_status",
      title: "기관별 데이터 현황",
      chartType: "custom",
      variable: "각 기관별 데이터 수집 건수를 전체, 30일간, 7일간 비교",
    };

    return [...pies, custom];
  }, []);

  const chartCounts = useMemo(() => {
    const counts = DASHBOARD_WIDGETS.reduce((acc, w) => {
      acc[w.chartType] = (acc[w.chartType] || 0) + 1;
      return acc;
    }, {});
    return { total: DASHBOARD_WIDGETS.length, types: counts };
  }, [DASHBOARD_WIDGETS]);

  // subject rows 로드 (통계용)
  useEffect(() => {
    if (!projectId) return;

    const fetchRows = async () => {
      try {
        const sRows = await formAPI.getSubjectList(projectId);
        setRows(sRows);

        const total = sRows.length;
        const now = Date.now();
        const recent7 = sRows.filter((r) => {
          const t = new Date(
            r.created_at || r.createdAt || r.date || 0
          ).getTime();
          return now - t <= 7 * 24 * 60 * 60 * 1000;
        }).length;
        const recent30 = sRows.filter((r) => {
          const t = new Date(
            r.created_at || r.createdAt || r.date || 0
          ).getTime();
          return now - t <= 30 * 24 * 60 * 60 * 1000;
        }).length;

        const siteCounts = {};
        sRows.forEach((r) => {
          const site = r.arrest_site || r.site || r.arrest_location || "기타";
          siteCounts[site] = (siteCounts[site] || 0) + 1;
        });

        setStats({ total, recent7, recent30, siteCounts });
      } catch (err) {
        console.error("Subject rows 가져오기 실패", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
  }, [projectId]);

  if (!project || loading) {
    return (
      <div style={{ padding: "24px" }}>프로젝트 정보를 불러오는 중...</div>
    );
  }

  // 담당자 정보 추출 (예: project.managers 배열)
  const managersRaw = project.managers || [];
  const fallbackManagers = [
    {
      name: "Chuck",
      email: "chuck@idea2m.com",
      phone: "24시간 언제든 문의를 남겨주세요.",
    },
  ];
  const managers = managersRaw.length ? managersRaw : fallbackManagers;

  return (
    <div className="vip-variable-container">
      {/* 기본 정보 */}
      <h2 className="vip-projectinfo-title">{project.name}</h2>
      <p className="vip-projectinfo-desc">
        {project.description || "설명이 없습니다."}
      </p>

      {/* 섹션 1: 프로젝트 담당자 배정 정보 */}
      <h3 className="section-title">프로젝트 담당자 배정</h3>
      {managers.length ? (
        (() => {
          const m = managers[0];
          return (
            <div className="manager-card">
              <span className="name">{m.name || "-"}</span>
              <span className="email">{m.email || "-"}</span>
              <span className="phone">{m.phone || "-"}</span>
            </div>
          );
        })()
      ) : (
        <p style={{ color: "#64748b", marginBottom: "32px" }}>
          담당자 정보가 없습니다.
        </p>
      )}

      {/* 섹션 2: 대쉬보드 설계 정보 */}
      <h3 className="section-title">대시보드 설계</h3>
      {DASHBOARD_WIDGETS.length ? (
        <table
          className="variable-table"
          style={{ maxWidth: "600px", marginBottom: "32px" }}
        >
          <thead>
            <tr>
              <th>위젯 제목</th>
              <th>차트 타입</th>
              <th>변수</th>
            </tr>
          </thead>
          <tbody>
            {DASHBOARD_WIDGETS.map((w, idx) => (
              <tr key={idx}>
                <td>{w.title || `위젯 ${idx + 1}`}</td>
                <td>{w.chartType || "-"}</td>
                <td>{w.variable || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ color: "#64748b", marginBottom: "32px" }}>
          설계된 대쉬보드 정보가 없습니다.
        </p>
      )}

      {/* 대시보드 설계 부가 설명 카드 */}
      <div className="dashboard-info-card">
        <p style={{ marginBottom: "8px" }}>
          의뢰 요건에 따른 대시보드 설계 내용입니다. 간단한 차트 17개와, 커스텀
          차트 1개로 구성이 되어 있습니다. <br></br>더 다양한 차트 및 커스텀
          차트가 필요할 경우 문의 탭을 이용해주세요.
        </p>
      </div>

      {/* 섹션 3: 플랜 정보 */}
      <h3 className="section-title">프로젝트 플랜</h3>
      <div className="plan-card">
        <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
          {project.plan || DEFAULT_PLAN}
        </p>
      </div>

      {/* 섹션 4: 참여 기관 정보 */}
      <h3 className="section-title">참여 기관</h3>
      <div className="institution-single-card">
        총 3개의 기관이 참여하고 있으며, 각 기관에서 사용을 위한 총 연구원 계정
        9개, 관리자 계정 3개를 배정했습니다. 계정에 대한 정보 문의 및 변경이
        필요할 시에는 <strong>문의 탭</strong>을 이용해주세요.
      </div>
    </div>
  );
};

export default VIP10ProjectInfo;
